import { UserStatus } from 'module/api/user/types';
import { ReactComponent as GoogleIcon } from 'module/authentication/components/saml-button/google.svg';
import { useTranslation } from 'module/hooks/use-translation';
import { ReactComponent as MicrosoftIcon } from 'module/ui/assets/svgs/microsoft.svg';
import { DatePicker } from 'module/ui/components/atoms/date-picker/date-picker';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Switch } from 'module/ui/components/atoms/switch/switch';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { Button, Dialog, Option as SelectOption, Select } from '@rocketlane/ui';
import { Radio } from 'antd';
import { EmailSettings, EmailIntelligenceEnabledTo } from 'api/email';
import moment from 'moment';
import { useNativeMembersQuery } from 'queries/member';
import { ReactNode, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

export enum EmailDuration {
  Past7Days = 'past_7_days',
  Past30Days = 'past_30_days',
  Past90Days = 'past_90_days',
  Past180Days = 'past_180_days',
  Past1Year = 'past_1_year',
  YearToDate = 'year_to_date',
  All = 'all',
  Custom = 'custom',
}

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  height: 100vh;
`;

const SettingRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BackSyncHeading = styled.div`
  font-size: var(--font-size-04);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-04);
`;

const FetchEmailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
`;

const FetchEmailsHeading = styled.div`
  font-size: var(--font-size-04);
  font-weight: var(--font-weight-semi-bold);
  line-height: var(--line-height-04);
`;

const SettingsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
  width: 70%;
`;

const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
  width: 30%;
`;

const DatePickerContainer = styled.div`
  margin-top: var(--spacing-02);
`;

const StyledDatePicker = styled(DatePicker)`
  visibility: hidden;
  height: 0;
  padding: 0;
  margin: 0;
  border: none;
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-04);
`;

const ModalContent = styled.div`
  text-align: center;
  padding: 20px 0;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const IconLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

const timePeriodOptions = [
  { label: 'Past 7 days', value: EmailDuration.Past7Days },
  { label: 'Past 30 days', value: EmailDuration.Past30Days },
  { label: 'Past 90 days', value: EmailDuration.Past90Days },
  { label: 'Past 180 days', value: EmailDuration.Past180Days },
  { label: 'Past year', value: EmailDuration.Past1Year },
  { label: 'Year-to-date', value: EmailDuration.YearToDate },
  { label: 'All available emails', value: EmailDuration.All },
  { label: 'Custom', value: EmailDuration.Custom },
];

export enum EmailProvider {
  Gmail = 'gmail',
  Outlook = 'outlook',
}

interface EmailProviderOption extends SelectOption {
  value: EmailProvider;
  icon: ReactNode;
}

const StyledModalTitle = styled(ModalTitle)`
  padding: 0 var(--spacing-03);
`;

const StyledModalContent = styled(ModalContent)`
  text-align: left;
`;

export const Settings: React.FC<{
  emailSettings: EmailSettings;
  onToggle?: (emailSettings: EmailSettings) => void;
  isModuleFed: boolean;
}> = ({ emailSettings, onToggle, isModuleFed }) => {
  const { data: memberData, isLoading: isMemberLoading } =
    useNativeMembersQuery();
  const googleWorkspaceLabel = useTranslation(
    'settings.email_intelligence.google_workspace',
  );
  const microsoftWorkspaceLabel = useTranslation(
    'settings.email_intelligence.microsoft_workspace',
  );
  const emailProviderOptions = useMemo<EmailProviderOption[]>(
    () => [
      {
        label: googleWorkspaceLabel,
        value: EmailProvider.Gmail,
        icon: <GoogleIcon width={12} height={12} />,
      },
      {
        label: microsoftWorkspaceLabel,
        value: EmailProvider.Outlook,
        icon: <MicrosoftIcon width={12} height={12} />,
      },
    ],
    [googleWorkspaceLabel, microsoftWorkspaceLabel],
  );

  const activeTeamMembers = useMemo(() => {
    if (!isMemberLoading) {
      return (
        memberData?.members?.filter(
          ({ user }) => user.userStatus !== UserStatus.Inactive,
        ) || []
      );
    }

    return [];
  }, [memberData, isMemberLoading]);

  const emailIntelligenceEnabledTeamMembers = useMemo(() => {
    if (isMemberLoading || memberData?.members == null) {
      return [];
    }

    return memberData.members.filter(
      ({ user }) => user.emailIntelligenceEnabled === true,
    );
  }, [memberData, isMemberLoading]);

  const [emailSettingsUIState, setEmailSettingsUIState] = useState({
    selectedTimePeriod:
      timePeriodOptions.find(
        (option) => option.value === emailSettings?.backSyncStartRange,
      ) ?? timePeriodOptions[2],
    customDate: null as moment.Moment | null,
    isCustomDateOpen: false,
    showConfirmation: false,
    pendingTimePeriod: null as any,
    isDropdownOpen: false,
    emailProviderInfo: emailSettings.emailProviderInfo ?? {
      gmail: EmailProvider.Gmail,
    },
    emailIntelligenceEnabledTo:
      emailSettings.emailIntelligenceEnabledTo ??
      EmailIntelligenceEnabledTo.SpecificTeamMembersOnly,
  });

  // const [providerDropdownOpen, setProviderDropdownOpen] = useState(false);

  const handleTimePeriodChange = (value: any) => {
    setEmailSettingsUIState((prev) => ({
      ...prev,
      pendingTimePeriod: value,
      showConfirmation: true,
    }));
  };
  const handleEmailProviderChange = (value: any) => {
    // Map select option to emailProviderInfo structure
    const newEmailProviderInfo =
      value.value === EmailProvider.Gmail
        ? { gmail: EmailProvider.Gmail }
        : { outlook: EmailProvider.Outlook };

    setEmailSettingsUIState((prev) => ({
      ...prev,
      emailProviderInfo: newEmailProviderInfo,
    }));

    const updatedSettings: EmailSettings = {
      ...emailSettings,
      emailProviderInfo: newEmailProviderInfo,
    };

    onToggle?.(updatedSettings);
  };

  const handleConfirmTimePeriodChange = () => {
    const { pendingTimePeriod, customDate } = emailSettingsUIState;

    if (pendingTimePeriod.value === EmailDuration.Custom) {
      setEmailSettingsUIState((prev) => ({
        ...prev,
        isCustomDateOpen: true,
        isDropdownOpen: false,
        selectedTimePeriod: pendingTimePeriod,
        showConfirmation: false,
        pendingTimePeriod: null,
      }));
    } else {
      setEmailSettingsUIState((prev) => ({
        ...prev,
        isCustomDateOpen: false,
        selectedTimePeriod: pendingTimePeriod,
        showConfirmation: false,
        pendingTimePeriod: null,
      }));
    }

    const updatedSettings = {
      ...emailSettings,
      backSyncStartRange: pendingTimePeriod.value,
      backSyncStartDate:
        pendingTimePeriod.value === EmailDuration.Custom
          ? customDate?.format('YYYY-MM-DD')
          : '',
    };

    if (pendingTimePeriod.value === EmailDuration.Custom) {
      return;
    }

    onToggle?.(updatedSettings as EmailSettings);
  };

  const handleCancelTimePeriodChange = () => {
    setEmailSettingsUIState((prev) => ({
      ...prev,
      showConfirmation: false,
      pendingTimePeriod: null,
    }));
  };

  const handleCustomDateChange = (date: moment.Moment | null) => {
    if (!date) {
      return;
    }

    // Disable future dates
    const today = moment().startOf('day');

    if (date.isAfter(today)) {
      return;
    }

    const updatedPeriodOptions = {
      ...timePeriodOptions[7],
      label: `${date.format('MMM D, YYYY')}`,
    };

    setEmailSettingsUIState((prev) => ({
      ...prev,
      customDate: date,
      selectedTimePeriod: updatedPeriodOptions,
      isCustomDateOpen: false,
      isDropdownOpen: false,
    }));

    // Update settings with the new custom date
    const updatedSettings = {
      ...emailSettings,
      backSyncStartRange: EmailDuration.Custom,
      backSyncStartDate: date.format('YYYY-MM-DD'),
    };

    onToggle?.(updatedSettings as EmailSettings);
  };

  const handleEmailIntelligenceScope = (e: any) => {
    const value = e.target.value as EmailIntelligenceEnabledTo;

    setEmailSettingsUIState((prev) => ({
      ...prev,
      emailIntelligenceEnabledTo: value,
    }));

    const updatedSettings: EmailSettings = {
      ...emailSettings,
      emailIntelligenceEnabledTo: value,
    };

    onToggle?.(updatedSettings);
  };

  const disabledDate = (current: moment.Moment) => {
    // Disable future dates
    return current && current.isAfter(moment().endOf('day'));
  };

  const getSelectedEmailProviderOption = () => {
    const providerInfo = emailSettings.emailProviderInfo;

    if (providerInfo && 'gmail' in providerInfo) {
      return emailProviderOptions[0];
    }

    if (providerInfo && 'outlook' in providerInfo) {
      return emailProviderOptions[1];
    }

    // fallback → Google
    return;
  };

  const isEmailProviderDisabled = () => {
    const selected = getSelectedEmailProviderOption()?.value;

    return (
      selected === EmailProvider.Gmail || selected === EmailProvider.Outlook
    );
  };

  return (
    <SettingsContainer>
      <SettingRow>
        <SwitchContainer>
          <Switch
            checked={emailSettings?.emailIntelligenceEnabled || false}
            onChange={(checked) =>
              onToggle?.({
                ...emailSettings,
                emailIntelligenceEnabled: checked,
              })
            }
            size="small"
          />
          <FormattedMessage id="settings.email_intelligence.enabled" />
        </SwitchContainer>
        {emailSettings?.emailIntelligenceEnabled && (
          <SettingsWrapper>
            <div>
              <BackSyncHeading>
                <FormattedMessage id="settings.email_intelligence.back_sync_start_date" />
              </BackSyncHeading>
              <FormattedMessage id="settings.email_intelligence.back_sync_start_date_description" />
            </div>
            <div>
              <FetchEmailsContainer>
                <FetchEmailsHeading>
                  <FormattedMessage id="settings.email_intelligence.fetch_emails" />
                </FetchEmailsHeading>
                <DropdownContainer>
                  <Select
                    value={emailSettingsUIState.selectedTimePeriod}
                    onChange={handleTimePeriodChange}
                    options={timePeriodOptions}
                    placeholder={
                      <FormattedMessage id="settings.email_intelligence.select_time_period" />
                    }
                    size="small"
                    isSearchable={false}
                    isDisabled={emailSettingsUIState.isCustomDateOpen}
                    menuIsOpen={
                      emailSettingsUIState.isDropdownOpen &&
                      !emailSettingsUIState.isCustomDateOpen
                    }
                    onMenuOpen={() =>
                      setEmailSettingsUIState((prev) => ({
                        ...prev,
                        isDropdownOpen: true,
                      }))
                    }
                    onMenuClose={() =>
                      setEmailSettingsUIState((prev) => ({
                        ...prev,
                        isDropdownOpen: false,
                      }))
                    }
                  />
                  {emailSettingsUIState.selectedTimePeriod?.value ===
                    EmailDuration.Custom && (
                    <DatePickerContainer>
                      <StyledDatePicker
                        value={emailSettingsUIState.customDate}
                        onChange={handleCustomDateChange}
                        size="small"
                        autoFocus
                        open={emailSettingsUIState.isCustomDateOpen}
                        inputReadOnly
                        disabledDate={disabledDate}
                      />
                    </DatePickerContainer>
                  )}
                </DropdownContainer>
              </FetchEmailsContainer>
            </div>
            <BackSyncHeading>
              <FormattedMessage id="settings.email_intelligence.email_configuration" />
            </BackSyncHeading>
            <div>
              <FetchEmailsHeading>
                <FormattedMessage id="settings.email_intelligence.email_provider_question" />
              </FetchEmailsHeading>
              <DropdownContainer>
                <Select<EmailProviderOption>
                  value={getSelectedEmailProviderOption()}
                  onChange={handleEmailProviderChange}
                  options={emailProviderOptions}
                  defaultValue={getSelectedEmailProviderOption()}
                  size="small"
                  isSearchable={false}
                  isDisabled={isEmailProviderDisabled()}
                  menuIsOpen={!isEmailProviderDisabled()}
                  formatOptionLabel={(option, { context }) =>
                    context === 'value' ? (
                      <IconLabelContainer>
                        {option.icon}
                        <span>{option.label}</span>
                      </IconLabelContainer>
                    ) : (
                      option.label
                    )
                  }
                />
              </DropdownContainer>
            </div>
            <div>
              <BackSyncHeading>
                <FormattedMessage id="settings.email_intelligence.email_provider_description" />
              </BackSyncHeading>
              <FormattedMessage id="settings.email_intelligence.email_team_members_description" />
              {isModuleFed && (
                <a
                  href="/settings/team-members"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FormattedMessage id="settings.email_intelligence.email_team_members_question" />
                </a>
              )}
            </div>
            <Radio.Group
              value={
                emailSettings.emailIntelligenceEnabledTo ??
                EmailIntelligenceEnabledTo.SpecificTeamMembersOnly
              }
              onChange={handleEmailIntelligenceScope}
              options={[
                {
                  label: (
                    <FormattedMessage
                      id={
                        (emailSettings.emailIntelligenceEnabledTo ??
                          EmailIntelligenceEnabledTo.SpecificTeamMembersOnly) ===
                        EmailIntelligenceEnabledTo.AllTeamMembers
                          ? 'settings.email_intelligence.email_team_members.all_with_count'
                          : 'settings.email_intelligence.email_team_members.all'
                      }
                      values={{
                        count: activeTeamMembers.length,
                      }}
                    />
                  ),
                  value: EmailIntelligenceEnabledTo.AllTeamMembers,
                },
                {
                  label: (
                    <FormattedMessage
                      id={
                        (emailSettings.emailIntelligenceEnabledTo ??
                          EmailIntelligenceEnabledTo.SpecificTeamMembersOnly) ===
                        EmailIntelligenceEnabledTo.SpecificTeamMembersOnly
                          ? 'settings.email_intelligence.email_team_members.specific_with_count'
                          : 'settings.email_intelligence.email_team_members.specific'
                      }
                      values={{
                        count: emailIntelligenceEnabledTeamMembers.length,
                      }}
                    />
                  ),
                  value: EmailIntelligenceEnabledTo.SpecificTeamMembersOnly,
                },
              ]}
            />
          </SettingsWrapper>
        )}
      </SettingRow>

      <Dialog
        open={emailSettingsUIState.showConfirmation}
        onCancel={handleCancelTimePeriodChange}
        width={500}
        title={
          <StyledModalTitle>
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <FormattedMessage id="settings.email_intelligence.confirm_change_title" />
          </StyledModalTitle>
        }
        content={
          <StyledModalContent>
            <FormattedMessage id="settings.email_intelligence.confirm_change_message" />
          </StyledModalContent>
        }
        footer={
          <ModalActions>
            <Button onClick={handleCancelTimePeriodChange}>Cancel</Button>
            <Button variant="danger" onClick={handleConfirmTimePeriodChange}>
              Update
            </Button>
          </ModalActions>
        }
      />
    </SettingsContainer>
  );
};
